import React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { REGEXP_ONLY_DIGITS } from 'input-otp';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useSmsVerifyCodeMutation } from '@/api/hooks/mutations/use-sms-verify-code-mutation';
import { useSmsVerifyStartMutation } from '@/api/hooks/mutations/use-sms-verify-start-mutation';
import { DataPrivacyStatement } from '@/components/DataPrivacyStatement';
import { GeneralErrorIndicator } from '@/components/GeneralErrorIndicator';
import { NavigationBar } from '@/components/NavigationBar';
import { SensitiveField } from '@/components/SensitiveField';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { useSessionSave } from '@/hooks/use-session-save';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store';
import { identifyUser } from '@/lib/analytics';
import { prettifyPhoneNumber } from '@/lib/formatters.ts';
import { logger } from '@/lib/logger';
import { useCreateOrder } from '@/views/hooks/create-order-context.tsx';

const formId = 'verify-phone-code-form';
const formSchema = z.object({
  code: z
    .string()
    .length(6, 'Code must be 6 digits')
    .regex(/^\d{6}$/, 'Code must contain only numbers'),
});

type VerifyPhoneCodeFormData = z.infer<typeof formSchema>;

export function VerifyPhoneCode() {
  const { saveSession } = useSessionSave();
  const navigate = useNavigate();
  const smsVerifyCodeMutation = useSmsVerifyCodeMutation();
  const smsVerifyStartMutation = useSmsVerifyStartMutation();
  const { createOrder } = useCreateOrder();
  const otpInputRef = React.useRef<HTMLInputElement>(null);

  const { contactPhoneNumber } = useSessionStore.getState();

  const form = useForm<VerifyPhoneCodeFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      code: '',
    },
  });

  const handleBack = () => {
    navigate({ to: '/verify-profile/phone-number' });
  };

  const handleResendCodeToPhone = async () => {
    if (!smsVerifyStartMutation.canResend) {
      logger.error(new Error('Cannot resend verification code'));

      return;
    }

    try {
      const response = await smsVerifyStartMutation.mutateAsync({
        phoneNumber: `+1${contactPhoneNumber}`,
      });

      if (!response.success) {
        throw new Error(response.message);
      }
    } catch (error) {
      logger.error('Failed to resend verification code', error);

      form.setError('code', {
        message: 'Failed to resend your verification code',
      });
    }

    form.setValue('code', '');
  };

  const onSubmit = async (formValues: VerifyPhoneCodeFormData) => {
    try {
      const response = await smsVerifyCodeMutation.mutateAsync({
        phoneNumber: `+1${contactPhoneNumber}`,
        code: formValues.code,
      });

      if (!response.success) {
        form.setValue('code', '');
        form.setError('code', { message: 'Invalid verification code' });

        return;
      }

      useSessionStore.getState().updateState({
        contactPhoneNumberVerified: true,
      });

      const { contactEmail: currentEmail } = useSessionStore.getState();

      if (currentEmail) {
        identifyUser(currentEmail);
      }

      saveSession();

      createOrder();

      navigate({ to: '/appointment/confirmed' });
    } catch (error) {
      logger.error('Failed to verify phone code', error);

      form.setValue('code', '');
      form.setError('code', { message: 'Invalid verification code' });
    }
  };

  React.useEffect(
    function focusInputWhenNotPending() {
      if (!smsVerifyCodeMutation.isPending) {
        otpInputRef.current?.focus();
      }
    },
    [smsVerifyCodeMutation.isPending]
  );

  return (
    <section>
      <NavigationBar onBack={handleBack} backLabel="Back" />
      <Card>
        <CardHeader>
          <CardTitle>We texted you a code</CardTitle>
        </CardHeader>
        <CardContent className="space-y-8 sm:space-y-12">
          <p className="text-main text-base">
            Sent to:{' '}
            <SensitiveField className="font-medium">
              {prettifyPhoneNumber(contactPhoneNumber)}
            </SensitiveField>
          </p>

          <form
            className="space-y-8 sm:space-y-12"
            onSubmit={form.handleSubmit(onSubmit)}
            id={formId}
          >
            <Controller
              name="code"
              control={form.control}
              render={({ field, fieldState }) => {
                const inputId = `${formId}-code`;

                return (
                  <Field orientation="vertical" data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={inputId}>Enter the 6-digit code</FieldLabel>
                    <InputOTP
                      // oxlint-disable-next-line no-autofocus
                      autoFocus={true}
                      disabled={smsVerifyCodeMutation.isPending}
                      minLength={6}
                      maxLength={6}
                      id={inputId}
                      ref={otpInputRef}
                      value={field.value}
                      onComplete={form.handleSubmit(onSubmit)}
                      pattern={REGEXP_ONLY_DIGITS}
                      inputMode="numeric"
                      onChange={field.onChange}
                      aria-invalid={fieldState.invalid}
                    >
                      <InputOTPGroup className="min-w-0 flex-1 gap-2 *:data-[slot=input-otp-slot]:h-[60px] *:data-[slot=input-otp-slot]:max-w-12 *:data-[slot=input-otp-slot]:min-w-0 *:data-[slot=input-otp-slot]:flex-1 *:data-[slot=input-otp-slot]:rounded-md *:data-[slot=input-otp-slot]:border">
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>

                    {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
                  </Field>
                );
              }}
            />

            {smsVerifyStartMutation.isError && (
              <GeneralErrorIndicator>
                Failed to resend verification code. Please try again.
              </GeneralErrorIndicator>
            )}

            <div className="space-y-4.5">
              <div className="flex justify-center sm:justify-start">
                <button
                  type="button"
                  onClick={handleResendCodeToPhone}
                  disabled={smsVerifyStartMutation.isPending || !smsVerifyStartMutation.canResend}
                  className="text-hydrangea-500 hover:text-hydrangea-700 text-sm font-medium underline underline-offset-auto transition-colors disabled:opacity-50"
                >
                  {getResendCodeLabel(smsVerifyStartMutation)}
                </button>
              </div>

              <Button
                type="submit"
                disabled={smsVerifyCodeMutation.isPending}
                className="w-full sm:w-auto sm:min-w-[186px]"
              >
                {smsVerifyCodeMutation.isPending ? 'Verifying...' : 'Confirm'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <DataPrivacyStatement />
    </section>
  );
}

function getResendCodeLabel(mutation: {
  isPending: boolean;
  canResend: boolean;
  remainingMs: number;
}) {
  if (mutation.isPending) return 'Sending code...';
  if (mutation.canResend) return "Don't see it? Resend SMS code";
  return `Don't see it? Resend SMS code in ${Math.ceil(mutation.remainingMs / 1000)}s`;
}
